import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Users, MessageCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

/** Wall of Heroes — public live counters + opted-in family stories. */
function StatTile({ icon: Icon, label, value, suffix = "", color = "var(--gold)" }) {
  return (
    <div className="brutal-card bg-white p-5">
      <span
        className="w-10 h-10 rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center mb-2"
        style={{ background: `var(${color})` }}
      >
        <Icon size={18} />
      </span>
      <div className="display text-4xl sm:text-5xl font-extrabold leading-none">
        {value}
        <span className="text-2xl font-bold text-[color:var(--ink-soft)]">{suffix}</span>
      </div>
      <div className="display text-xs uppercase tracking-[0.2em] mt-2 text-[color:var(--ink-soft)] font-bold">
        {label}
      </div>
    </div>
  );
}

export default function Community() {
  const [stats, setStats] = useState(null);
  const [families, setFamilies] = useState([]);

  useSEO({
    title: "Wall of Heroes — the families building SonSpeak together",
    description:
      "Live counters and family stories from the SonSpeak community. Every family's voice grows louder when another family joins.",
    path: "/community",
  });

  useEffect(() => {
    (async () => {
      try {
        const [s, f] = await Promise.all([
          api.get("/community/stats"),
          api.get("/families?limit=24"),
        ]);
        setStats(s.data);
        setFamilies(f.data?.items || []);
      } catch (err) {
        // Page still renders the marketing copy/CTAs gracefully without stats.
        console.warn("Community: stats/families fetch failed", err?.response?.status || err?.message);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen dotted-bg" data-testid="community-page">
      <main className="max-w-6xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
        <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3">
          Wall of heroes
        </div>
        <h1 className="display text-4xl sm:text-6xl font-extrabold leading-tight mb-3">
          Look what we built together.
        </h1>
        <p className="text-lg text-[color:var(--ink-soft)] max-w-2xl mb-10">
          Every counter below is a real family choosing communication over silence.
          You can be part of the next number.
        </p>

        {stats && typeof stats.total_communications === "number" && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="community-stats">
            <StatTile icon={Users}        label="Families"               value={(stats.families ?? 0).toLocaleString()} color="--gold" />
            <StatTile icon={MessageCircle} label="Communications spoken" value={stats.total_communications.toLocaleString()} color="--gold" />
            <StatTile icon={Heart}        label="Clinicians hand-reviewed" value={(stats.therapists_approved ?? 0).toLocaleString()} color="--coral" />
          </div>
        )}

        {families.length > 0 && (
          <section data-testid="community-families">
            <h2 className="display text-2xl sm:text-3xl font-extrabold mb-1">Family ambassadors</h2>
            <p className="text-[color:var(--ink-soft)] mb-5">
              Real parents who opted in to share their story. Tap one to read it.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {families.map((f) => (
                <Link
                  key={f.slug}
                  to={`/families/${f.slug}`}
                  className="brutal-card bg-white p-4 hover:bg-[color:var(--paper-muted)] transition-colors"
                  data-testid={`community-family-${f.slug}`}
                >
                  <div className="display text-lg font-extrabold">
                    {f.caregiver_first_name}'s family
                  </div>
                  <div className="text-xs text-[color:var(--ink-soft)] mt-1 flex items-center gap-1">
                    <Heart size={12} className="text-[color:var(--coral)]" /> Sharing their story
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="brutal-card bg-[color:var(--cream-deep)] p-6 sm:p-8 mt-12 text-center">
          <Heart size={28} className="mx-auto text-[color:var(--coral)] fill-[color:var(--coral)] mb-3" />
          <h2 className="display text-2xl sm:text-3xl font-extrabold mb-2">
            Add your family to the wall.
          </h2>
          <p className="text-[color:var(--ink-soft)] max-w-xl mx-auto mb-5">
            One short caregiver story can be the lifeline another parent finds at 2am.
            Your child's name and photo stay private — your story is what helps.
          </p>
          <Link to="/profiles" className="pill-btn primary text-base py-3">
            Share my story <ArrowRight size={16} />
          </Link>
        </section>
      </main>
    </div>
  );
}
